import React, { useRef, useCallback } from 'react'
import { useA11yId } from '../../utils/a11y'
import type { MatchAnalysisTab } from './types'

interface MatchAnalysisTabNavProps {
  activeTab: MatchAnalysisTab
  onTabChange: (tab: MatchAnalysisTab) => void
  panelIdPrefix?: string
}

const TABS: { id: MatchAnalysisTab; label: string }[] = [
  { id: 'teams', label: 'Teams' },
  { id: 'lineup', label: 'Lineup' },
  { id: 'h2h', label: 'H2H' },
  { id: 'form', label: 'Form' },
  { id: 'table', label: 'Table' },
  { id: 'ai', label: 'AI Insights' },
]

export function MatchAnalysisTabNav({
  activeTab,
  onTabChange,
  panelIdPrefix = 'ma-tabpanel',
}: MatchAnalysisTabNavProps) {
  const tablistId = useA11yId('ma-tablist')
  const tabRefs = useRef<Map<MatchAnalysisTab, HTMLButtonElement>>(new Map())

  const setTabRef = useCallback(
    (id: MatchAnalysisTab, el: HTMLButtonElement | null) => {
      if (el) {
        tabRefs.current.set(id, el)
      } else {
        tabRefs.current.delete(id)
      }
    },
    []
  )

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault()
        newIndex = currentIndex > 0 ? currentIndex - 1 : TABS.length - 1
        break
      case 'ArrowRight':
        e.preventDefault()
        newIndex = currentIndex < TABS.length - 1 ? currentIndex + 1 : 0
        break
      case 'Home':
        e.preventDefault()
        newIndex = 0
        break
      case 'End':
        e.preventDefault()
        newIndex = TABS.length - 1
        break
      default:
        return
    }

    const newTab = TABS[newIndex]
    onTabChange(newTab.id)
    const tabEl = tabRefs.current.get(newTab.id)
    tabEl?.focus()
  }

  return (
    <div
      id={tablistId}
      className="tabs"
      role="tablist"
      aria-label="Match analysis sections"
    >
      {TABS.map((tab, index) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            ref={(el) => setTabRef(tab.id, el)}
            id={`ma-tab-${tab.id}`}
            role="tab"
            aria-selected={isActive}
            aria-controls={`${panelIdPrefix}-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            className={`tab ${isActive ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            type="button"
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

interface TabPanelProps {
  id: MatchAnalysisTab
  activeTab: MatchAnalysisTab
  children: React.ReactNode
  className?: string
  idPrefix?: string
}

export function MATabPanel({
  id,
  activeTab,
  children,
  className,
  idPrefix = 'ma-tabpanel',
}: TabPanelProps) {
  const isActive = activeTab === id

  if (!isActive) return null

  return (
    <div
      id={`${idPrefix}-${id}`}
      role="tabpanel"
      aria-labelledby={`ma-tab-${id}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  )
}
